import * as React from 'react'
import { ChampionIcon } from './champion-icon'
import { cn } from '@/lib/utils'

import {
  BRACKET_COLUMN_GAP,
  BRACKET_COLUMN_WIDTH,
  BRACKET_TOP_PADDING,
  VERTICAL_HEADER_CONTENT_GAP,
  VERTICAL_HEADER_RAIL_WIDTH,
  VERTICAL_MIN_BRACKET_WIDTH,
} from './constants'
import type {
  BracketDirection,
  BracketMakerCopy,
  BracketTheme,
  ColumnHeader,
} from './types'

type ColumnHeaderRailProps = {
  bracketTitle: string
  champion: string
  columnHeaders: ColumnHeader[]
  columnOffsets: number[]
  columnWidths: number[]
  copy: BracketMakerCopy
  direction: BracketDirection
  roundCount: number
  showNames: boolean
  theme: BracketTheme
  verticalContentWidth: number
  verticalReverse: boolean
  verticalYAxisLength: number
  verticalXOffset: number
}

export const ColumnHeaderRail = React.memo(function ColumnHeaderRail({
  bracketTitle,
  champion,
  columnHeaders,
  columnOffsets,
  columnWidths,
  copy,
  direction,
  roundCount,
  showNames,
  theme,
  verticalContentWidth,
  verticalReverse,
  verticalYAxisLength,
  verticalXOffset,
}: ColumnHeaderRailProps) {
  if (direction === 'vertical') {
    return (
      <div className="absolute inset-0 text-center">
        <h2
          className="absolute top-6 text-4xl font-bold leading-tight"
          style={{
            left: verticalXOffset,
            width: Math.max(verticalContentWidth, VERTICAL_MIN_BRACKET_WIDTH),
          }}
        >
          {bracketTitle}
        </h2>
        <div className="absolute inset-y-0 left-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {columnHeaders.map((header, headerIndex) => {
            const columnOffset = columnOffsets[header.columnIndex - 1] ?? 0
            const headerHeight =
              columnWidths[header.columnIndex - 1] ?? BRACKET_COLUMN_WIDTH
            const headerTop =
              BRACKET_TOP_PADDING +
              (verticalReverse
                ? verticalYAxisLength - columnOffset - headerHeight
                : columnOffset)

            return (
              <div
                key={`label-${headerIndex}`}
                style={{
                  left: Math.max(
                    0,
                    verticalXOffset -
                      VERTICAL_HEADER_CONTENT_GAP -
                      VERTICAL_HEADER_RAIL_WIDTH,
                  ),
                  top: headerTop + headerHeight / 2,
                  transform: 'translateY(-50%)',
                  width: VERTICAL_HEADER_RAIL_WIDTH,
                }}
                className={cn(
                  theme.roundHeader,
                  'absolute gap-2 py-1.5',
                  header.isChampion
                    ? theme.roundHeaderChampion
                    : header.roundIndex === roundCount - 1
                      ? theme.roundHeaderFinal
                      : theme.roundHeaderStandard,
                )}
              >
                <span>{header.label}</span>
                {header.isChampion ? (
                  <span className="bracket-text bracket-card flex items-center gap-1 rounded-full bg-background px-2 py-0.5 text-[10px] font-semibold text-foreground/80">
                    <ChampionIcon size={14} />
                    <span className="line-clamp-1 max-w-[140px]">
                      {showNames && champion ? champion : copy.champion}
                    </span>
                  </span>
                ) : (
                  <span className={theme.roundHeaderBadge}>
                    {header.matchCount}{' '}
                    {header.matchCount === 1
                      ? copy.matchSingular
                      : copy.matchPlural}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="absolute inset-x-0 top-4 flex flex-col items-center gap-8 text-center">
      <h2 className="text-4xl font-bold leading-tight">{bracketTitle}</h2>
      <div
        className="flex text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        style={{ gap: BRACKET_COLUMN_GAP }}
      >
        {columnHeaders.map((header, headerIndex) => (
          <div
            key={`label-${headerIndex}`}
            style={{
              width:
                columnWidths[(header.columnIndex ?? headerIndex + 1) - 1] ??
                BRACKET_COLUMN_WIDTH,
            }}
            className={cn(
              theme.roundHeader,
              header.isChampion
                ? theme.roundHeaderChampion
                : header.roundIndex === roundCount - 1
                  ? theme.roundHeaderFinal
                  : theme.roundHeaderStandard,
            )}
          >
            <span>{header.label}</span>
            {header.isChampion ? (
              <span className="bracket-text bracket-card flex items-center gap-1 rounded-full bg-background px-2 py-0.5 text-[10px] font-semibold text-foreground/80">
                <ChampionIcon size={14} />
                <span className="line-clamp-1 max-w-[140px]">
                  {showNames && champion ? champion : copy.champion}
                </span>
              </span>
            ) : (
              <span className={theme.roundHeaderBadge}>
                {header.matchCount}{' '}
                {header.matchCount === 1
                  ? copy.matchSingular
                  : copy.matchPlural}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
})
